import { AxiosResponse } from 'axios';
import { setLang } from 'i18n';
import * as Sentry from '@sentry/react';
import ApiInstance, { setAuthToken } from 'api/Base';
import { v4 as uuidv4 } from 'uuid';
import i18n from 'i18next';
import { IUserData } from 'modules/Auth/hooks/@types';
import { LOCAL_STORAGE_AUTH_TOKEN, LOCAL_STORAGE_USER_ID } from 'common/localStorageKeys';
import EventReporter, { setUserAttributes } from 'lib/EventsProvider';
import mixpanel from 'mixpanel-browser';
import Leanplum from 'leanplum-sdk';
import IEventProviders from 'lib/EventsProvider/@types';
import appEvents from 'appEvents';
import UserService from 'services/User/User.service';
import { VARIABLES_DEFAULT } from 'hooks/useLeanplum/values';
import { clearLocalStore, setUserLocalStorage } from 'helpers';
import { API_URL, APP_INFO } from 'config';
import { URL_CREATE_USER_FROM_QUIZ, URL_EXTERNAL_TOKEN, URL_SIGNUP } from './Auth.values';
import { ICreateUserFromQuizParams, ISignUpParams, IVerifyExternalTokenParams } from './@types';

class AuthService {
  static instance: AuthService;

  static getInstance(): AuthService {
    if (!AuthService.instance) AuthService.instance = new AuthService();
    return AuthService.instance;
  }

  static setAuthDataWithToken = ({ token, user }: IUserData): void => {
    Leanplum.setUserId(`${user.id}`);
    Leanplum.setVariables(VARIABLES_DEFAULT);
    setAuthToken(token);
    setUserAttributes({ source: 'KIN' });

    mixpanel.identify(user?.id?.toString());

    Sentry.setUser({ id: user?.id, email: user?.email });
    setUserAttributes({
      $email: user?.email,
      userEmail: user?.email,
      kineduLanguage: user?.locale,
      kineduCountry: user?.country,
      webAppVersion: APP_INFO.version,
    });
    setUserLocalStorage(user);
    setLang(user?.locale || 'en');
  };

  static setAuth = ({ token, user }: IUserData): void => {
    clearLocalStore();
    window.localStorage.setItem(LOCAL_STORAGE_AUTH_TOKEN, token);
    window.localStorage.setItem(LOCAL_STORAGE_USER_ID, user?.id?.toString());
    Leanplum.setUserId(`${user.id}`);
    Leanplum.setVariables(VARIABLES_DEFAULT);
    setAuthToken(token);
    setUserAttributes({ source: 'KIN' });

    mixpanel.alias(user.id?.toString());
    mixpanel.identify(user?.id?.toString());
    Sentry.setUser({ id: user?.id, email: user?.email });
    setUserAttributes({
      $email: user?.email,
      userEmail: user?.email,
      kineduLanguage: user?.locale,
      kineduCountry: user?.country,
      webAppVersion: APP_INFO.version,
    });
    setUserLocalStorage(user);
    setLang(user?.locale || 'en');
    EventReporter.action(appEvents.COMPLETE_REGISTRATION(), [IEventProviders.MIXPANEL]);
    EventReporter.action(appEvents.SIGNUP(), [IEventProviders.MIXPANEL]);
    UserService.setAlias({ id: user?.id?.toString() })
      .then(console.info)
      .catch(console.info);
    // Leanplum.setUserId(`${user.id}`);
    // Leanplum.setVariables(VARIABLES_DEFAULT);
  };

  static signUp = ({
    signal,
    email,
    password,
    apiVersion = 3,
    locale = i18n.language,
    fbUUID,
    fbc,
    fbp,
  }: ISignUpParams): Promise<AxiosResponse> =>
    ApiInstance.post(
      URL_SIGNUP,
      { email, password, api_version: apiVersion, locale, fb_event_id: fbUUID, fbc, fbp },
      {
        signal,
        headers: {
          Authorization: null,
        },
      },
    )
      .then((response) => {
        this.setAuth(response.data.data);
        return response;
      })
      .catch((error) => {
        EventReporter.action(appEvents.SIGNUP_ERROR(error?.response?.data?.error_data), [IEventProviders.MIXPANEL]);
        // throw error;
        return Promise.reject(error);
      });

  static verifyExternalToken = ({
    signal,
    token,
    fbUUID,
    fbc,
    fbp,
  }: IVerifyExternalTokenParams): Promise<AxiosResponse> =>
    ApiInstance.post(
      URL_EXTERNAL_TOKEN,
      { token, fb_event_id: fbUUID || uuidv4(), fbc, fbp },
      {
        signal,
        headers: {
          apiVersion: API_URL.versions.V4,
          Authorization: null,
        },
      },
    )
      .then((response) => response)
      .catch((error) => Promise.reject(error));

  static createUserFromQuiz = ({
    signal,
    email,
    fbUUID,
    fbc,
    fbp,
  }: ICreateUserFromQuizParams): Promise<AxiosResponse> =>
    ApiInstance.post(
      URL_CREATE_USER_FROM_QUIZ,
      { email, fb_event_id: fbUUID, fbc, fbp },
      {
        signal,
        headers: {
          apiVersion: API_URL.versions.V4,
          // Authorization: null,
          // '5d2562a90d24f12a2d1feea21879174f13e1c695daa9cb3cd24a47dac72b6ea8f4e781223a7c42674257e2944c822b6cf35d9994aaa6a82d57f239279ed31847'
          Authorization: `Token token=${localStorage.getItem('tokenCreate')}`,
        },
      },
    )
      .then((response) => response)
      .catch((error) => Promise.reject(error));
}

export default AuthService;
